// Shared helpers for turning the AI report API's structured `metrics` object
// (asr_change, downloads, ku, ktr, user_rating, review_volume, revenue) into
// display-ready card data. Used by AIReportDisplay, AIReportSummaryTab and
// PublicAIReportsTab so the three surfaces stay in sync.

export const METRIC_DEFS = [
  { key: "asr_change", label: "ASR Change" },
  { key: "downloads", label: "Downloads", isRange: true },
  { key: "ku", label: "KU" },
  { key: "ktr", label: "KTR" },
  { key: "user_rating", label: "User Rating" },
  { key: "review_volume", label: "Review Volume" },
  { key: "revenue", label: "Revenue", isRange: true, prefix: "$" },
]

const DIRECTION_MAP = { up: "up", down: "down", stable: "neutral" }

export const formatNumber = num => {
  if (num === null || num === undefined || isNaN(num)) return "0"
  const abs = Math.abs(num)
  const decimals = abs !== 0 && abs < 1 ? 4 : abs < 10 ? 2 : 0
  let str = num.toFixed(decimals)
  if (str.includes(".")) str = str.replace(/0+$/, "").replace(/\.$/, "")
  return str
}

// Abbreviates a magnitude to its own natural bracket (1.2K / 3.4M / 1.1B), the
// same way the animated single-value metric cards do. Each endpoint of a range
// is formatted independently — forcing both endpoints into a shared unit
// causes nonsense once one side crosses a bracket the other hasn't reached yet
// (e.g. deriving "K" from the low end of "623000-1414000" would render the
// high end as "1414K" instead of promoting it to "1.41M").
const formatCompactNumber = (num, prefix = "") => {
  const abs = Math.abs(num)
  if (abs >= 1_000_000_000) return `${prefix}${formatNumber(abs / 1_000_000_000)}B`
  if (abs >= 1_000_000) return `${prefix}${formatNumber(abs / 1_000_000)}M`
  if (abs >= 1_000) return `${prefix}${formatNumber(abs / 1_000)}K`
  return `${prefix}${Math.round(abs).toLocaleString()}`
}

export const formatRange = (rangeStr, prefix = "") => {
  if (!rangeStr) return "0"
  const rawParts = String(rangeStr).split(/[-–—]/).map(s => s.trim()).filter(Boolean)
  const formatted = rawParts.map(p => {
    const num = parseFloat(p.replace(/[^0-9.]/g, ""))
    return isNaN(num) ? p : formatCompactNumber(num, prefix)
  })
  return formatted.join(" – ")
}

// Turns the API's { asr_change, downloads, ku, ... } metrics object into card-ready entries
export const buildMetricCards = metrics => {
  if (!metrics || typeof metrics !== "object") return []
  return METRIC_DEFS.reduce((cards, def) => {
    const data = metrics[def.key]
    if (!data) return cards

    const direction = DIRECTION_MAP[data.direction] || "neutral"
    const percentChange = typeof data.percent_change === "number" ? data.percent_change : null
    const changeValue = percentChange !== null ? `${formatNumber(Math.abs(percentChange))}%` : "0%"

    let displayValue
    if (def.isRange) {
      displayValue = formatRange(data.current, def.prefix)
    } else if (def.key === "user_rating") {
      displayValue = formatNumber(data.end_value)
    } else {
      displayValue = formatNumber(Math.abs(data.absolute_change ?? 0))
    }

    const trendWord = direction === "up" ? "increased" : direction === "down" ? "decreased" : "remained stable"

    cards.push({
      label: def.label,
      value: displayValue,
      change: changeValue,
      direction,
      detail: `${def.label} ${trendWord} by ${changeValue} over the analysis period.`,
      isRange: !!def.isRange,
    })
    return cards
  }, [])
}
